import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import API_URL from "../../config/ApiUrl";

// Fetch picklists with filters and pagination
export const getPicklists = createAsyncThunk(
  "picklist/getPicklists",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      dispatch(setActivePayload(payload));
      const filteredPayload = Object.fromEntries(
        Object.entries(payload).filter(
          ([, value]) => value !== null && value !== undefined && value !== "",
        ),
      );
      const params = new URLSearchParams();

      for (const [key, value] of Object.entries(filteredPayload)) {
        if (Array.isArray(value)) {
          value.forEach((item) => {
            params.append(key, item);
          });
        } else {
          params.append(key, value);
        }
      }
      const queryString = params.toString();
      const { data } = await axios.get(
        `${API_URL.PICKLIST.SLUG}?${queryString}`,
      );
      const picklist_list = data.data.list;
      const totalRecords = data.data.totalRecords;
      return { picklist_list, data: { totalRecords } };
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

// Get single picklist by ID
export const getPicklistById = createAsyncThunk(
  "picklist/getPicklistById",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`${API_URL.PICKLIST.SLUG}/${id}`);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

// Get picklist items
export const getPicklistItems = createAsyncThunk(
  "picklist/getPicklistItems",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(API_URL.PICKLIST.GET_ITEMS(id));
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

// Prepare picklist data (for create page)
export const preparePicklist = createAsyncThunk(
  "picklist/preparePicklist",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(API_URL.PICKLIST.PREPARE, payload);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

// Get eligible sales orders for picklist creation
// Requires: customerId, locationId, businessWarehouseId (all required)
export const getEligibleSalesOrders = createAsyncThunk(
  "picklist/getEligibleSalesOrders",
  async (
    { customerId, locationId, businessWarehouseId },
    { rejectWithValue },
  ) => {
    try {
      const params = new URLSearchParams({
        customerId,
        locationId,
        businessWarehouseId,
      });
      const { data } = await axios.get(
        `${API_URL.PICKLIST.ELIGIBLE_SALES_ORDERS}?${params.toString()}`,
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

// Fetch pending sales orders (Pending Orders tab)
export const getPendingSalesOrders = createAsyncThunk(
  "picklist/getPendingSalesOrders",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      dispatch(setPendingSOActivePayload(payload));
      const filteredPayload = Object.fromEntries(
        Object.entries(payload).filter(
          ([, value]) => value !== null && value !== undefined && value !== "",
        ),
      );
      const params = new URLSearchParams();

      for (const [key, value] of Object.entries(filteredPayload)) {
        if (Array.isArray(value)) {
          value.forEach((item) => {
            params.append(key, item);
          });
        } else {
          params.append(key, value);
        }
      }
      const queryString = params.toString();
      const { data } = await axios.get(
        `${API_URL.PICKLIST.PENDING_SALES_ORDERS}?${queryString}`,
      );
      const pending_sales_orders_list = data.data.list;
      const totalRecords = data.data.totalRecords;
      return { pending_sales_orders_list, data: { totalRecords } };
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

// Create new picklist
export const createPicklist = createAsyncThunk(
  "picklist/createPicklist",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(API_URL.PICKLIST.SLUG, payload);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

// Update picklist (Draft status only)
// API: PUT /BackOffice/Picklist/{picklistId}
export const updatePicklist = createAsyncThunk(
  "picklist/updatePicklist",
  async ({ picklistId, payload }, { rejectWithValue }) => {
    try {
      const { data } = await axios.put(
        `${API_URL.PICKLIST.SLUG}/${picklistId}`,
        payload,
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

// Delete picklist
export const deletePicklist = createAsyncThunk(
  "picklist/deletePicklist",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await axios.delete(`${API_URL.PICKLIST.SLUG}/${id}`);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

// Start picking (change status from Draft to InProgress)
// API: POST /BackOffice/Picklist/{picklistId:guid}/start
export const startPicking = createAsyncThunk(
  "picklist/startPicking",
  async (picklistId, { rejectWithValue, dispatch, getState }) => {
    try {
      const { data } = await axios.post(
        API_URL.PICKLIST.START_PICKING(picklistId),
      );
      // Refresh detail if viewing the same picklist
      const detailId =
        getState().picklist.picklist_detail?.picklistId ||
        getState().picklist.picklist_detail?.id;
      if (detailId === picklistId) {
        dispatch(getPicklistById(picklistId));
      }
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

// Mark as picked (update picked quantities)
// API: POST /BackOffice/Picklist/{picklistId:guid}/pick
// New contract: { assigneeUserId?, notes?, picklistDate?, items: [{ picklistItemId, pickedQuantity, binId? }] }
export const markAsPicked = createAsyncThunk(
  "picklist/markAsPicked",
  async ({ picklistId, payload }, { rejectWithValue, dispatch, getState }) => {
    try {
      const { data } = await axios.post(
        API_URL.PICKLIST.PICK(picklistId),
        payload,
      );
      // Refresh detail if viewing the same picklist
      const detailId =
        getState().picklist.picklist_detail?.picklistId ||
        getState().picklist.picklist_detail?.id;
      if (detailId === picklistId) {
        dispatch(getPicklistById(picklistId));
      }
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

// Cancel picklist
// API: POST /BackOffice/Picklist/{picklistId:guid}/cancel
export const cancelPicklist = createAsyncThunk(
  "picklist/cancelPicklist",
  async (picklistId, { rejectWithValue, dispatch, getState }) => {
    try {
      const { data } = await axios.post(API_URL.PICKLIST.CANCEL(picklistId));
      // Refresh detail if viewing the same picklist
      const detailId =
        getState().picklist.picklist_detail?.picklistId ||
        getState().picklist.picklist_detail?.id;
      if (detailId === picklistId) {
        dispatch(getPicklistById(picklistId));
      }
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

const initialState = {
  picklist_list: [],
  picklist_detail: null,
  picklist_items: [],
  prepared_items: [],
  eligible_sales_orders: [], // Grouped by shipping address
  current_values: null,
  active_payload: null,
  total_records: 0,
  pending_sales_orders_list: [],
  pending_so_total_records: 0,
  pending_so_active_payload: null,
};

const picklistSlice = createSlice({
  name: "picklist",
  initialState,
  reducers: {
    setCurrentValues(state, action) {
      state.current_values = action.payload;
    },
    setActivePayload(state, action) {
      state.active_payload = action.payload;
    },
    resetCurrentValue(state) {
      state.current_values = null;
    },
    setPreparedItems(state, action) {
      state.prepared_items = action.payload;
    },
    resetPicklistDetail(state) {
      state.picklist_detail = null;
      state.picklist_items = [];
    },
    clearEligibleSalesOrders(state) {
      state.eligible_sales_orders = [];
    },
    setPendingSOActivePayload(state, action) {
      state.pending_so_active_payload = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPicklists.fulfilled, (state, action) => {
        state.picklist_list = action.payload.picklist_list;
        state.total_records = action.payload.data?.totalRecords || 0;
      })
      .addCase(getPicklistById.fulfilled, (state, action) => {
        state.picklist_detail = action.payload;
      })
      .addCase(getPicklistItems.fulfilled, (state, action) => {
        state.picklist_items = action.payload;
      })
      .addCase(preparePicklist.fulfilled, (state, action) => {
        state.prepared_items = action.payload;
      })
      .addCase(getEligibleSalesOrders.fulfilled, (state, action) => {
        state.eligible_sales_orders = action.payload;
      })
      .addCase(getPendingSalesOrders.fulfilled, (state, action) => {
        state.pending_sales_orders_list = action.payload.pending_sales_orders_list;
        state.pending_so_total_records = action.payload.data?.totalRecords || 0;
      });
  },
});

export const {
  setCurrentValues,
  setActivePayload,
  resetCurrentValue,
  setPreparedItems,
  resetPicklistDetail,
  clearEligibleSalesOrders,
  setPendingSOActivePayload,
} = picklistSlice.actions;

export default picklistSlice.reducer;
